import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../../components/Loader";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import {
  convertStringToArray,
  extractDate,
  sanitizeString,
} from "../../Utils/utils";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import { useGetProtectedApiDataQuery } from "../../redux/services/apiSlice";
import { imageUrl } from "../../constants/constants";
import Viewer from "react-viewer";

const ArchivedNewsDetailPage = () => {
  const { languageType } = useSelector((state) => state.homeSlice);
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const { getText } = useLanguageTranslation();
  const { nid } = useParams();
  const {
    data: archivedData = [],
    error,
    isLoading,
  } = useGetProtectedApiDataQuery({
    url: "archive_latest_news",
    lang: languageType,
  });

  const transformedData = useMemo(() => {
    return archivedData.map((item) => ({
      ...item,
      field_upload_image: convertStringToArray(item.field_upload_image),
    }));
  }, [archivedData]);

  const selectedNews = transformedData.find((news) => news.nid === nid);

  const selectedTitle = sanitizeString(selectedNews?.title);
  const selectedDescription = sanitizeString(selectedNews?.body);

  const lastUpdatedOn = useMemo(() => {
    return extractDate(selectedNews);
  }, [selectedNews]);

  const modifiedObj = {
    ...selectedNews,
    field_download: convertStringToArray(selectedNews?.field_download),
  };

  const handleImageClick = (image) => {
    setVisible(true);
    setCurrentImages([
      {
        src: imageUrl + image.field_upload_image[0].file_path,
        alt: selectedTitle,
      },
    ]);
    setActiveIndex(0);
  };

  const file_ext = modifiedObj?.field_latest_news_video?.slice(-3);

  if (!selectedNews) {
    return <p>News with id {nid} not found</p>;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <p>Error: {error.message}</p>;
  }

  return (
    <>
      <InnerBannerBreadcrumb
        bannerImage="/images/inner_aboutbann.jpg"
        title={getText("Archived News", "संग्रहीत समाचार")}
        titleLink="/archived-news"
        titleTwo={selectedTitle}
        levelTwo={true}
        levelThree={false}
        isActive={selectedTitle}
      />

      <div className="inner-container">
        <div className="container">
          <div className="title-sec"></div>

          <section className="news-detail">
            <div className="container">
              <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
              <div className="row mb-5">
                <div className="col-12 col-sm-12 col-md-3 col-lg-3">
                  <div
                    className="card-sec"
                    onClick={() => handleImageClick(selectedNews)}
                  >
                    <div className="img-sec">
                      <img
                        src={
                          selectedNews.field_upload_image?.[0]?.file_path
                            ? imageUrl +
                              selectedNews.field_upload_image[0].file_path
                            : "images/dummy.png"
                        }
                        alt={selectedTitle}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-12 col-md-9 col-lg-9">
                  <div className="title-sec">
                    <h4>{selectedTitle}</h4>
                  </div>
                  <div className="about_inntxt">
                    <p>{selectedDescription}</p>
                  </div>
                  {modifiedObj.field_latest_news_video && file_ext === "mp4" ? (
                    <>
                      <div className="title4">
                        <h4>Video</h4>
                      </div>
                      <video controls>
                        <source
                          src={`${process.env.REACT_APP_IMAGE_URL}/neepco/sites/default/files/2024-07/${selectedNews.field_latest_news_video}`}
                          type="audio/mpeg"
                        />
                      </video>
                    </>
                  ) : modifiedObj.field_latest_news_video &&
                    file_ext === "mp3" ? (
                    <>
                      <div className="title4">
                        <h4>Audio</h4>
                      </div>
                      <audio controls>
                        <source
                          src={`${process.env.REACT_APP_IMAGE_URL}/neepco/sites/default/files/2024-07/${selectedNews.field_latest_news_video}`}
                          type="audio/mpeg"
                        />
                      </audio>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
                {modifiedObj.field_download.length > 0 && (
                  <div className="col-12 col-sm-12 col-md-12 col-lg-12 mt-4">
                    <div className="table-responsive">
                      <table className="table table-striped custom-table">
                        <thead className="thead-dark">
                          <tr>
                            <th>S. No.</th>
                            <th>Description</th>
                            <th>Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modifiedObj?.field_download.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.title}</td>
                              <td>
                                <a
                                  href={`${process.env.REACT_APP_IMAGE_URL}/${item?.file_path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <div className="pdf-icon">
                                    <img
                                      src="./images/pdf-icon.png"
                                      alt="pdf-icon"
                                      className="mr-2"
                                    />
                                  </div>
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
          <Viewer
            visible={visible}
            onClose={() => setVisible(false)}
            onMaskClick={() => setVisible(false)}
            images={currentImages}
            activeIndex={activeIndex}
            rotatable={false}
            zoomable={true}
            scalable={false}
            drag={false}
            // downloadable={true}
          />
        </div>
      </div>
    </>
  );
};

export default ArchivedNewsDetailPage;
